import { useEffect, useState } from "react";
import { PageHeader, StatCard, SectionCard, Pill } from "@/components/ui-bits";
import { GraduationCap, UsersRound, Library, BookOpen, ClipboardList, FileQuestion, TrendingUp, Trophy } from "lucide-react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState([]);
  const [recentResults, setRecentResults] = useState([]);

  useEffect(() => {
    api.get("/stats/overview").then(r => setStats(r.data));
    api.get("/stats/assessment-performance").then(r => setPerf(r.data));
    api.get("/results").then(r => setRecentResults(r.data.slice(0, 6)));
  }, []);

  return (
    <div>
      <PageHeader
        title="Welcome back, Admin"
        subtitle="A high-level view of the institute — employees, trainers, courses and assessment outcomes."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Employees" value={stats?.students ?? "—"} icon={GraduationCap} accent="blue" testId="stat-card-students" />
        <StatCard label="Trainers" value={stats?.trainers ?? "—"} icon={UsersRound} accent="purple" testId="stat-card-trainers" />
        <StatCard label="Courses" value={stats?.courses ?? "—"} icon={Library} accent="amber" testId="stat-card-courses" />
        <StatCard label="Batches" value={stats?.batches ?? "—"} icon={BookOpen} accent="green" testId="stat-card-batches" />
        <StatCard label="Assessments" value={stats?.assessments ?? "—"} icon={ClipboardList} accent="blue" testId="stat-card-assessments" />
        <StatCard label="Questions" value={stats?.questions ?? "—"} icon={FileQuestion} accent="slate" testId="stat-card-questions" />
        <StatCard label="Attempts" value={stats?.results ?? "—"} icon={TrendingUp} accent="purple" testId="stat-card-attempts" />
        <StatCard label="Pass rate" value={`${stats?.pass_rate ?? 0}%`} icon={Trophy} accent="green" testId="stat-card-pass-rate" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2">
          <SectionCard title="Assessment performance">
            {perf.length === 0 ? (
              <div className="text-sm text-slate-500 py-10 text-center">No results yet. Employees need to submit assessments to populate this chart.</div>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={perf}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="title" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 0 }} />
                    <Bar dataKey="avg" name="Avg %" fill="#2563EB" />
                    <Bar dataKey="passed" name="Passed" fill="#22C55E" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </SectionCard>
        </div>
        <SectionCard title="Recent submissions">
          {recentResults.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No submissions yet.</div>
          ) : (
            <ul className="space-y-3">
              {recentResults.map(r => (
                <li key={r.id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-slate-900">{r.student_name}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[200px]">{r.assessment_title}</div>
                  </div>
                  <Pill tone={r.status === "passed" ? "green" : "red"}>{r.percentage}% · {r.status}</Pill>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
